import type { ButtonPriority, ButtonSize } from '@timelyapp/react';
import { Button } from '@timelyapp/react';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import Input from './Input';
import InputDecorator from './InputDecorator';

import styles from './TypeToConfirm.module.scss';

interface Props {
  confirmString: string;
  confirmButtonProps: {
    text: string;
    icon?: React.ReactElement;
    size?: ButtonSize;
    priority: ButtonPriority;
    destructive?: boolean;
    isLoading?: boolean;
    disabled?: boolean;
    'data-testid'?: string;
  };
  onChange?: (confirmStringMatches: boolean) => void;
  onConfirm: () => void;
  className?: string;
  // accept the confirmString in any form, or make sure it matches the specified case?
  caseSensitive?: boolean;
}

const TypeToConfirm: React.FC<Props> = ({
  onChange,
  confirmString,
  confirmButtonProps,
  className,
  onConfirm,
  caseSensitive = false,
}) => {
  const { text, icon, disabled, ...rest } = confirmButtonProps;
  const [input, setInput] = useState('');
  const matchesConfirmString = useCallback(
    (value: string) =>
      caseSensitive ? value === confirmString : value.toLowerCase() === confirmString.toLowerCase(),
    [caseSensitive, confirmString]
  );
  const hasMatchedConfirmString = matchesConfirmString(input);
  const handleChangeInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setInput(value);
      onChange?.(matchesConfirmString(value));
    },
    [matchesConfirmString, onChange]
  );

  return (
    <div className={classNames(styles.container, className)}>
      <InputDecorator>
        <Input
          type="text"
          required
          name="confirmString"
          value={input}
          onChange={handleChangeInput}
          style={{ padding: '11px 14px' }}
        />
      </InputDecorator>
      <Button
        disabled={disabled || !hasMatchedConfirmString}
        size="medium"
        onClick={onConfirm}
        {...rest}
      >
        {icon != null && <span>{icon}</span>}
        <span>{text}</span>
      </Button>
    </div>
  );
};

export default TypeToConfirm;
